import { LawTerm } from "@/models/law-term/LawTerm";
import { MatchType, isInLinkOrOverlap } from "./LawTermProcessor";

// Split tooltip header lines from body
export function splitMarkdownHeaderAndBody(markdownText: string) {
  const parts = markdownText.split(/\n{2,}/);
  const headerLines = parts
    .filter((p) => p.trim().startsWith("[tooltip_"))
    .join("\n\n");
  const bodyLines = parts
    .filter((p) => !p.trim().startsWith("[tooltip_"))
    .join("\n\n");
  return { headerLines, bodyLines };
}

// Create term & lemma maps from law dictionary
export function createTermAndLemmaMaps(dictionary: LawTerm[]) {
  const termMap: Record<string, { term: string; definition: string }> = {};
  const lemmaToOriginalMap: Record<string, string> = {};

  dictionary.forEach((lawTerm) => {
    termMap[lawTerm.term.toLowerCase()] = {
      term: lawTerm.term,
      definition: lawTerm.definition,
    };

    if (lawTerm.lemmatizedTerm && Array.isArray(lawTerm.lemmatizedTerm)) {
      lawTerm.lemmatizedTerm.forEach((lemma: string) => {
        lemmaToOriginalMap[lemma.toLowerCase()] = lawTerm.term;
      });
    }
  });

  return { termMap, lemmaToOriginalMap };
}

// Extract markdown links so we don't wrap inside links
export function getLinkRangesFromMarkdown(markdownText: string) {
  const linkRanges: Array<{ start: number; end: number }> = [];
  const linkRegex = /\[[^\]]+?\]\([^)]+\)|\[[^\]]+?\]\[tooltip_[^\]]+\]/g;
  let match;
  while ((match = linkRegex.exec(markdownText)) !== null) {
    linkRanges.push({ start: match.index, end: linkRegex.lastIndex });
  }
  return linkRanges;
}

// Tokenize text into words w/ position info
export function extractWordPositions(markdownText: string) {
  const wordRegex = /\b\w+\b/g;
  const results: Array<{ word: string; start: number; end: number }> = [];
  let match;
  while ((match = wordRegex.exec(markdownText)) !== null) {
    results.push({
      word: match[0],
      start: match.index,
      end: match.index + match[0].length,
    });
  }
  return results;
}

// Resolve each word into its lemma
export function lemmatizeWordList(
  wordMatches: { word: string; start: number; end: number }[],
  lemmatizedText:
    | { word: string; lemmatizedWord: string }[]
    | Record<string, string>
) {
  return wordMatches.map(({ word, start, end }) => {
    const lower = word.toLowerCase();
    const lemma = Array.isArray(lemmatizedText)
      ? lemmatizedText.find((e) => e.word.toLowerCase() === lower)
          ?.lemmatizedWord
      : lemmatizedText?.[lower] || lower;
    return {
      word,
      lemma: lemma ? lemma.toLowerCase() : word.toLowerCase(),
      start,
      end,
    };
  });
}

// Map lemmatized phrases to LawTerm
export function buildLemmatizedTermLookup(dictionary: LawTerm[]) {
  const lookup: Record<string, LawTerm> = {};
  dictionary.forEach((term) => {
    if (term.lemmatizedTerm && Array.isArray(term.lemmatizedTerm)) {
      const key = term.lemmatizedTerm.map((s) => s.toLowerCase()).join(" ");
      lookup[key] = term;
    }
  });
  return lookup;
}

// Match multi-word lemmas in sliding window
export function matchMultiWordLemmas(
  markdownText: string,
  lemmatizedWordList: {
    word: string;
    lemma: string;
    start: number;
    end: number;
  }[],
  lemmatizedTermLookup: Record<string, LawTerm>,
  linkRanges: { start: number; end: number }[],
  matches: MatchType[]
) {
  const MAX_WINDOW = 5;
  for (let i = 0; i < lemmatizedWordList.length; i++) {
    for (
      let w = 2;
      w <= MAX_WINDOW && i + w <= lemmatizedWordList.length;
      w++
    ) {
      const slice = lemmatizedWordList.slice(i, i + w);
      const phrase = slice.map((w) => w.lemma).join(" ");
      const lawTerm = lemmatizedTermLookup[phrase];
      if (!lawTerm) continue;

      const start = slice[0].start;
      const end = slice[w - 1].end;
      if (!isInLinkOrOverlap(start, end, linkRanges, matches)) {
        matches.push({
          start,
          end,
          term: markdownText.slice(start, end),
          originalTerm: lawTerm.term,
          definition: lawTerm.definition,
        });
      }
    }
  }
}

// Match single words via lemmatization
export function matchSingleWordLemmas(
  markdownText: string,
  lemmatizedText:
    | { word: string; lemmatizedWord: string }[]
    | Record<string, string>,
  termMap: Record<string, { term: string; definition: string }>,
  lemmaToOriginalMap: Record<string, string>,
  linkRanges: { start: number; end: number }[],
  matches: MatchType[]
) {
  const words = markdownText.split(/(\s+|\b)/);
  let pos = 0;

  for (const word of words) {
    const trimmed = word.trim();
    if (!trimmed) {
      pos += word.length;
      continue;
    }

    const alreadyMatched = matches.some((m) => pos >= m.start && pos < m.end);
    if (alreadyMatched) {
      pos += word.length;
      continue;
    }

    const lower = trimmed.toLowerCase();
    const lemma = Array.isArray(lemmatizedText)
      ? lemmatizedText.find((e) => e.word.toLowerCase() === lower)
          ?.lemmatizedWord || lower
      : lemmatizedText?.[lower] || lower;

    let lawTerm = termMap[lemma.toLowerCase()];
    let originalTerm = lawTerm?.term || lemmaToOriginalMap[lemma.toLowerCase()];

    if (!lawTerm) {
      const forms = ["er", "e", "s", "ne", "r"];
      for (const f of forms) {
        const stripped = lemma.toLowerCase().replace(new RegExp(f + "$"), "");
        if (termMap[stripped]) {
          lawTerm = termMap[stripped];
          originalTerm = lawTerm.term;
          break;
        }
      }
    }

    if (
      lawTerm &&
      originalTerm &&
      !isInLinkOrOverlap(pos, pos + word.length, linkRanges, matches)
    ) {
      matches.push({
        start: pos,
        end: pos + word.length,
        term: word,
        originalTerm,
        definition: lawTerm.definition,
      });
    }

    pos += word.length;
  }
}

// Match terms by case-insensitive variations
export function matchCaseVariations(
  markdownText: string,
  termMap: Record<string, { term: string; definition: string }>,
  linkRanges: { start: number; end: number }[],
  matches: MatchType[]
) {
  Object.keys(termMap).forEach((termKey) => {
    const pattern = new RegExp(`\\b${termKey}(er|e|s|ne|r)?\\b`, "gi");
    let match;
    while ((match = pattern.exec(markdownText)) !== null) {
      const start = match.index;
      const end = start + match[0].length;
      if (!isInLinkOrOverlap(start, end, linkRanges, matches)) {
        matches.push({
          start,
          end,
          term: match[0],
          originalTerm: termMap[termKey].term,
          definition: termMap[termKey].definition,
        });
      }
    }
  });
}

// Remove overlapping matches (prefer longer)
export function removeOverlappingMatches(matches: MatchType[]) {
  matches.sort((a, b) => a.start - b.start);
  const toRemove = new Set<number>();

  for (let i = 0; i < matches.length; i++) {
    for (let j = i + 1; j < matches.length; j++) {
      if (toRemove.has(j)) continue;
      const a = matches[i],
        b = matches[j];
      if (!(b.end <= a.start || b.start >= a.end)) {
        if (a.end - a.start >= b.end - b.start) {
          toRemove.add(j);
        } else {
          toRemove.add(i);
          break;
        }
      }
    }
  }

  return matches.filter((_, i) => !toRemove.has(i));
}

// Replace matched terms with tooltip markdown
export function applyTooltipReplacements(
  markdownText: string,
  matches: MatchType[],
  tooltipReferences: string[],
  uniqueTerms: Set<string>
) {
  let result = markdownText;
  let offset = 0;

  matches.forEach(({ start, end, term, originalTerm, definition }) => {
    const label = originalTerm.toLowerCase().replace(/\s+/g, "-");
    const replacement = `[${term}][tooltip_${label}]`;

    result =
      result.slice(0, start + offset) +
      replacement +
      result.slice(end + offset);
    offset += replacement.length - term.length;

    if (!uniqueTerms.has(label)) {
      uniqueTerms.add(label);
      tooltipReferences.push(
        `[tooltip_${label}]: ## "${definition}|${originalTerm}"`
      );
    }
  });

  return result;
}

export function wrapLemmatizedTermsWithTooltip(
  markdownText: string,
  dictionary: LawTerm[],
  lemmatizedText?:
    | { word: string; lemmatizedWord: string }[]
    | Record<string, string>
): string {
  const { headerLines, bodyLines } = splitMarkdownHeaderAndBody(markdownText);
  markdownText = bodyLines;

  const { termMap, lemmaToOriginalMap } = createTermAndLemmaMaps(dictionary);
  const uniqueTerms = new Set<string>();
  const tooltipReferences: string[] = [];

  const linkRanges = getLinkRangesFromMarkdown(markdownText);
  const matches: MatchType[] = [];

  if (lemmatizedText) {
    const wordMatches = extractWordPositions(markdownText);
    const lemmatizedWordList = lemmatizeWordList(wordMatches, lemmatizedText);

    const lemmatizedTermLookup = buildLemmatizedTermLookup(dictionary);
    matchMultiWordLemmas(
      markdownText,
      lemmatizedWordList,
      lemmatizedTermLookup,
      linkRanges,
      matches
    );
    matchSingleWordLemmas(
      markdownText,
      lemmatizedText,
      termMap,
      lemmaToOriginalMap,
      linkRanges,
      matches
    );
    matchCaseVariations(markdownText, termMap, linkRanges, matches);
  }

  const filteredMatches = removeOverlappingMatches(matches);
  const resultText = applyTooltipReplacements(
    markdownText,
    filteredMatches,
    tooltipReferences,
    uniqueTerms
  );

  return `${headerLines}\n\n${tooltipReferences.join("\n")}\n\n${resultText}`;
}
